import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { AsyncTypeahead } from 'react-bootstrap-typeahead'
import Trash from 'react-feather/dist/icons/trash.js'
import conf from '../../../../../conf.js'
import api from '../../../../../../api.js'

const LanguagesSection = ({
  isOpen,
  toggleSection,
  combinedLanguages,
  openLanguages,
  toggleLanguage,
  selectedLanguages,
  handleSelectionChange,
  handleLanguageInputChange,
  handleDelete,
  isRegistrationFlow,
}) => {
  const [options, setOptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const handleSearch = async (query) => {
    setIsLoading(true)
    try {
      const response = await api.languages.suggest({ search: query })
      setOptions(response.data)
    } catch (e) {
      console.error(e)
      setOptions([])
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="mb-5">
      <div className="mb-3 d-flex align-items-center" onClick={() => toggleSection('languages')} style={{ cursor: 'pointer' }}>
        <span className="form-title-chip rounded-circle mr-2">7</span>

        <h4 className="mb-0">Langues</h4>

        <span className="ml-auto h3 text-secondary mb-0">{isOpen ? '-' : '+'}</span>
      </div>

      { isOpen && (
        <>
          {combinedLanguages.length === 0 ? (
            <div className="callout callout-secondary text-center col-md-7 mx-auto">
              Aucune langue à comparer.
            </div>
          ) : (
            combinedLanguages.map((lang, index) => (
              <div key={lang._id} className="callout py-3">
                <div className="d-flex align-items-center" style={{ cursor: 'pointer' }}>
                  <div className="d-flex align-items-start mr-4 flex-grow-1" onClick={() => toggleLanguage(lang._id)}>
                    <>
                      {lang.isIdentical && <span className="badge badge-success mt-1 mr-3">Identique</span>}
                      {lang.isDifferent && <span className="badge badge-info mt-1 mr-3">Différent</span>}
                      {lang.isNew && <span className="badge badge-primary text-white mt-1 mr-3">Nouveau</span>}
                      {lang.isCurrent && <span className="badge badge-secondary mt-1 mr-3">Twineeds</span>}
                    </>

                    <h5 className="mb-0">{lang.name || `Langue (ID: ${lang.analyseData?.languageId || lang.freelanceData?.languageId})`}</h5>
                  </div>

                  <div className="ml-auto d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm btn-round mr-3"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete('languages', index)
                      }}
                    >
                      <Trash/>
                    </button>

                    <span className="h4 text-secondary mb-0" onClick={() => toggleLanguage(lang._id)}>{openLanguages[lang._id] ? '-' : '+'}</span>
                  </div>
                </div>

                { openLanguages[lang._id] && (
                  <div className="row mt-3">
                    {/* Analyse Data Column */}
                    {lang.analyseData && (
                      <div className={`col-md-${(lang.isCurrent || lang.isDifferent) ? '6' : '12'}`}>
                        <div className={`p-3 mb-3 border rounded ${lang.isDifferent && selectedLanguages[lang._id] !== 'analyseData' ? 'border-light' : ''}`}>
                          {lang.isDifferent && (
                            <div className="custom-control custom-switch mb-2">
                              <input
                                className="custom-control-input"
                                type="checkbox"
                                id={`lang-${lang._id}-analyse`}
                                checked={selectedLanguages[lang._id] === 'analyseData'}
                                onChange={() => handleSelectionChange('languages', lang._id, 'analyseData')}
                              />

                              <label className="custom-control-label" htmlFor={`lang-${lang._id}-analyse`}>
                                Nouvelle donnée du CV
                              </label>
                            </div>
                          )}

                          {lang.analyseData && (
                            <div style={{ opacity: lang.isDifferent && selectedLanguages[lang._id] !== 'analyseData' ? 0.5 : 1 }}>
                              <div className="row">
                                <div className="col-md-8">
                                  <div className="form-group mb-2">
                                    <label>Langue</label>
                                    <AsyncTypeahead
                                      id={`lang-${lang._id}-analyse-name`}
                                      isLoading={isLoading}
                                      labelKey="name"
                                      onSearch={handleSearch}
                                      options={options}
                                      placeholder="Entrez le nom d'une langue"
                                      selected={lang.analyseData.name ? [{ name: lang.analyseData.name, id: lang.analyseData.languageId }] : []}
                                      onChange={(selected) => {
                                        if (selected.length > 0) {
                                          handleLanguageInputChange(index, 'analyseData', 'name', selected[0].name)
                                          handleLanguageInputChange(index, 'analyseData', 'languageId', selected[0].id)
                                        } else {
                                          handleLanguageInputChange(index, 'analyseData', 'name', '')
                                          handleLanguageInputChange(index, 'analyseData', 'languageId', null)
                                        }
                                      }}
                                      disabled={selectedLanguages[lang._id] !== 'analyseData'}
                                    />
                                  </div>
                                </div>

                                <div className="col-md-4">
                                  <div className="form-group mb-0">
                                    <label htmlFor={`lang-${lang._id}-analyse-level`}>Niveau</label>
                                    <select
                                      className="form-control"
                                      id={`lang-${lang._id}-analyse-level`}
                                      value={lang.analyseData.level}
                                      onChange={(e) => handleLanguageInputChange(index, 'analyseData', 'level', parseInt(e.target.value, 10))}
                                      disabled={selectedLanguages[lang._id] !== 'analyseData'}
                                    >
                                      {Object.values(conf.languageLevels).map((level) => (
                                        <option value={level.value} key={level.value}>{level.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Freelance Data Column */}
                    {(lang.isCurrent || lang.isDifferent) && (
                      <div className={`col-md-${lang.isDifferent ? '6' : '12'}`}>
                        <div className={`p-3 mb-3 border rounded ${lang.isDifferent && selectedLanguages[lang._id] !== 'freelanceData' ? 'border-light' : ''}`}>
                          {lang.isDifferent && (
                            <div className="custom-control custom-switch mb-2">
                              <input
                                className="custom-control-input"
                                type="checkbox"
                                id={`lang-${lang._id}-freelance`}
                                checked={selectedLanguages[lang._id] === 'freelanceData'}
                                onChange={() => handleSelectionChange('languages', lang._id, 'freelanceData')}
                              />

                              <label className="custom-control-label" htmlFor={`lang-${lang._id}-freelance`}>
                                Donnée actuelle
                              </label>
                            </div>
                          )}

                          {lang.freelanceData && (
                            <div style={{ opacity: lang.isDifferent && selectedLanguages[lang._id] !== 'freelanceData' ? 0.5 : 1 }}>
                              <div className="row">
                                <div className="col-md-8">
                                  <div className="form-group mb-2">
                                    <label>Langue</label>
                                    <AsyncTypeahead
                                      id={`lang-${lang._id}-freelance-name`}
                                      isLoading={isLoading}
                                      labelKey="name"
                                      onSearch={handleSearch}
                                      options={options}
                                      placeholder="Entrez le nom d'une langue"
                                      selected={lang.freelanceData.name ? [{ name: lang.freelanceData.name, id: lang.freelanceData.languageId }] : []}
                                      onChange={(selected) => {
                                        if (selected.length > 0) {
                                          handleLanguageInputChange(index, 'freelanceData', 'name', selected[0].name)
                                          handleLanguageInputChange(index, 'freelanceData', 'languageId', selected[0].id)
                                        } else {
                                          handleLanguageInputChange(index, 'freelanceData', 'name', '')
                                          handleLanguageInputChange(index, 'freelanceData', 'languageId', null)
                                        }
                                      }}
                                      disabled={selectedLanguages[lang._id] !== 'freelanceData'}
                                    />
                                  </div>
                                </div>

                                <div className="col-md-4">
                                  <div className="form-group mb-0">
                                    <label htmlFor={`lang-${lang._id}-freelance-level`}>Niveau</label>
                                    <select
                                      className="form-control"
                                      id={`lang-${lang._id}-freelance-level`}
                                      value={lang.freelanceData.level}
                                      onChange={(e) => handleLanguageInputChange(index, 'freelanceData', 'level', parseInt(e.target.value, 10))}
                                      disabled={selectedLanguages[lang._id] !== 'freelanceData'}
                                    >
                                      {Object.values(conf.languageLevels).map((level) => (
                                        <option value={level.value} key={level.value}>{level.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </>
      )}
    </div>
  )
}

LanguagesSection.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggleSection: PropTypes.func.isRequired,
  combinedLanguages: PropTypes.array.isRequired,
  openLanguages: PropTypes.object.isRequired,
  toggleLanguage: PropTypes.func.isRequired,
  selectedLanguages: PropTypes.object.isRequired,
  handleSelectionChange: PropTypes.func.isRequired,
  handleLanguageInputChange: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  isRegistrationFlow: PropTypes.bool.isRequired,
}

LanguagesSection.defaultProps = {
  isRegistrationFlow: false,
}

export default LanguagesSection
